import type { ProblemDetails } from "@fp/admin-api-client";
import { DataRefreshEvent, FsmPlatform } from "@fp/admin-api-client";
import {
  getListFsmConnectionsQueryKey,
  useGetFsmConnectionById,
  useListFsmConnections,
  useTestFsmConnection,
} from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import { useIssueSkimmerPairingCode } from "@fp/admin-api-client/skimmer-connections/skimmer-connections.client";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
  PageContentContainer,
} from "@fp/ui/components/base/page-layout/page-layout";
import { useClipboard } from "@fp/ui/hooks/use-clipboard";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Zap,
} from "@untitledui/icons";
import type { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { useDataRefresh } from "@/hooks/useSignalR";
import { toast } from "@/lib/toast";
import { CompletedWorkCard, type CompletedWorkCardCopy } from "./-components/completed-work-card";
import { AutoApprovalSetting } from "./-components/auto-approval-setting";
import { ConnectionCardHeader } from "./-components/connection-card-header";
import { platformPresentation } from "./-components/platform-presentation";
import type { PayoutTriggersCopy } from "./-components/payout-triggers-section";
import { SetupChecklist, type SetupChecklistCopy } from "./-components/setup-checklist";
import { TechsCard, type TechsCardCopy } from "./-components/techs-card";
import { SyncIssuesCard } from "./-components/sync-issues-card";
import type { SyncSwitchCopy } from "./-components/sync-switch";
import { useRefreshConnectionOnVisit } from "./-components/use-refresh-connection-on-visit";

export const Route = createFileRoute("/integrations/skimmer")({
  component: RouteComponent,
});

function CopyButton({ value }: { value: string }) {
  const { copied, copy } = useClipboard();

  const handleCopy = async () => {
    const { success } = await copy(value);

    if (success) {
      toast.success("Pairing code copied");
    } else {
      toast.error("Failed to copy the pairing code");
    }
  };

  return (
    <Button size="sm" color="secondary" onClick={handleCopy}>
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

interface IssuedPairingCode {
  code: string;
  lifetimeMs: number;
  rollsOverAtMs: number;
}

const PAIRING_CODE_RETRY_DELAY_MS = 5000;

function PairingCodeCard() {
  const queryClient = useQueryClient();
  const [pairingCode, setPairingCode] = useState<IssuedPairingCode | null>(null);
  const [retryAtMs, setRetryAtMs] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const progressBarRef = useRef<HTMLDivElement>(null);

  const { mutate: issuePairingCode } = useIssueSkimmerPairingCode({
    mutation: {
      onSuccess: (response) => {
        const lifetimeMs =
          new Date(response.data.rollsOverAt).getTime() -
          new Date(response.data.issuedAt).getTime();
        const receivedAtMs = Date.now();

        setNow(receivedAtMs);
        setPairingCode({
          code: response.data.code,
          lifetimeMs,
          rollsOverAtMs: receivedAtMs + lifetimeMs,
        });
      },
      onError: (error: AxiosError<ProblemDetails | undefined>) => {
        if (error.response?.status === 409) {
          queryClient.invalidateQueries({
            queryKey: getListFsmConnectionsQueryKey(),
          });
          return;
        }

        setRetryAtMs(Date.now() + PAIRING_CODE_RETRY_DELAY_MS);
        toast.error(
          (error.response?.data as ProblemDetails | undefined)?.title ??
            "Failed to generate a pairing code",
        );
      },
    },
  });

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(interval);
  }, [pairingCode]);

  useEffect(() => {
    issuePairingCode();
  }, [issuePairingCode]);

  useEffect(() => {
    if (pairingCode === null) {
      return;
    }

    const rollover = setTimeout(
      () => issuePairingCode(),
      Math.max(pairingCode.rollsOverAtMs - Date.now(), 0),
    );

    return () => clearTimeout(rollover);
  }, [pairingCode, issuePairingCode]);

  useEffect(() => {
    if (retryAtMs === null) {
      return;
    }

    const retry = setTimeout(
      () => issuePairingCode(),
      Math.max(retryAtMs - Date.now(), 0),
    );

    return () => clearTimeout(retry);
  }, [retryAtMs, issuePairingCode]);

  useEffect(() => {
    const progressBar = progressBarRef.current;

    if (!progressBar || pairingCode === null) {
      return;
    }

    const remainingMs = Math.max(pairingCode.rollsOverAtMs - Date.now(), 0);
    const animation = progressBar.animate(
      [
        { transform: `scaleX(${remainingMs / pairingCode.lifetimeMs})` },
        { transform: "scaleX(0)" },
      ],
      { duration: remainingMs, easing: "linear", fill: "forwards" },
    );

    return () => animation.cancel();
  }, [pairingCode]);

  const secondsLeft =
    pairingCode === null
      ? 0
      : Math.max(Math.ceil((pairingCode.rollsOverAtMs - now) / 1000), 1);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-gray-900">Connect Skimmer</h2>
        <Badge size="sm" color="blue">
          Awaiting activation
        </Badge>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Enter this pairing code when you activate FieldPay in Skimmer's
        marketplace. This page updates automatically once Skimmer completes the
        connection.
      </p>
      {pairingCode === null ? (
        <p className="text-sm text-gray-500">Generating a pairing code…</p>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <code className="font-mono text-4xl font-semibold tracking-widest text-gray-900">
              {`${pairingCode.code.slice(0, 3)} ${pairingCode.code.slice(3)}`}
            </code>
            <CopyButton value={pairingCode.code} />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {secondsLeft === 1
              ? "Expires in 1 second"
              : `Expires in ${secondsLeft} seconds`}
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              ref={progressBarRef}
              className="h-full w-full origin-left rounded-full bg-brand-600"
            />
          </div>
        </>
      )}
    </div>
  );
}

function ConnectionCard({ connectionId }: { connectionId: string }) {
  const { data: connectionResponse } = useGetFsmConnectionById(connectionId);
  const connection = connectionResponse?.data;

  const testMutation = useTestFsmConnection({
    mutation: {
      onSuccess: (response) => {
        if (response.data.isSuccess) {
          toast.success("Connection verified");
        } else {
          toast.error(
            "Skimmer rejected the connection. Disconnect and activate FieldPay in Skimmer's marketplace again.",
          );
        }
      },
      onError: () => {
        toast.error(
          "Something went wrong testing the connection. Please try again.",
        );
      },
    },
  });

  if (!connection) {
    return null;
  }

  return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <ConnectionCardHeader connectionId={connectionId} platformName="Skimmer" syncSwitchCopy={SKIMMER_SYNC_SWITCH_COPY} />
        <p className="text-sm text-gray-500 mb-4">
          Connected to Skimmer. FieldPay reads completed work from the Skimmer
          account that activated it.
        </p>
        <Button
          size="sm"
          color="secondary"
          onClick={() => testMutation.mutate({ id: connectionId })}
          isLoading={testMutation.isPending}
        >
          <span className="flex items-center gap-1">
            <Zap className="size-4" />
            Test
          </span>
        </Button>
        <AutoApprovalSetting
          connectionId={connectionId}
          description="When on, payout requests from Skimmer work are approved as soon as they are created. When off, payout requests wait in the approval queue for review."
        />
      </div>
  );
}

const SKIMMER_COMPLETED_WORK_COPY: CompletedWorkCardCopy = {
  syncWindowHelp:
    "This sets how far back Preview and Sync now look for completed work. Automatic syncing only picks up work completed after sync was turned on — use Sync now to reach further back.",
  previewFoundNothing: "Preview found no completed work in the sync window.",
  previewFoundNothingNew:
    "Preview found no new work — everything in the sync window has already been processed.",
  syncNowTitle: "Sync from Skimmer now",
  syncNowMessage: (days) =>
    `${days === 0 ? "This runs a real sync over today's completed work." : `This runs a real sync over the last ${days} ${days === 1 ? "day" : "days"} of completed work.`}`,
  syncFoundNothing: "Sync found no completed work in the sync window.",
  syncFoundNothingNew:
    "Sync found no new work — everything in the sync window has already been processed.",
};

const SKIMMER_SYNC_SWITCH_COPY: SyncSwitchCopy = {
  turnOnSyncTitle: "Turn on Skimmer sync",
  turnOnSyncMessage:
    "Sync imports completed work that matches your payout triggers.",
};

const SKIMMER_PAYOUT_TRIGGERS_COPY: PayoutTriggersCopy = {
  description:
    "Choose which Skimmer route stops and work order types trigger payouts. Only the selected route stops and work order types create payout requests.",
  loadFailed: "Failed to load payout triggers from Skimmer.",
  noneAvailable: "Nothing found in your Skimmer account that can trigger a payout.",
  noneSelected: "Nothing selected — no work will trigger payouts.",
};

const SKIMMER_TECHS_COPY: TechsCardCopy = {
  description: "The techs list shows every Skimmer tech FieldPay knows about and which techs FieldPay can pay. A tech is payable once Pay is on and the tech is linked to a talent member who has finished payout setup or to an active organization on your roster.",
  payDescription:
    "On means FieldPay pays the tech for completed work. Every new Skimmer tech starts on.",
  autoInviteTooltip:
    "FieldPay emails an invitation to every Skimmer tech with Pay on who has an email and is not a talent member yet.",
  autoInviteDescription:
    "Invite every Skimmer tech with Pay on who is not a talent member yet.",
  notPayableReasons: {},
};

const SKIMMER_SETUP_CHECKLIST_COPY: SetupChecklistCopy = {
  payableTechLabel: "At least one tech has Pay on",
};

function ConnectedView({ connectionId }: { connectionId: string }) {
  useRefreshConnectionOnVisit(connectionId);

  return (
    <div className="space-y-6">
      <SetupChecklist
        connectionId={connectionId}
        copy={SKIMMER_SETUP_CHECKLIST_COPY}
      />
      <ConnectionCard connectionId={connectionId} />
      <TechsCard
        connectionId={connectionId}
        platformName="Skimmer"
        copy={SKIMMER_TECHS_COPY}
      />
      <CompletedWorkCard
        connectionId={connectionId}
        platformName="Skimmer"
        copy={SKIMMER_COMPLETED_WORK_COPY}
        payoutTriggersCopy={SKIMMER_PAYOUT_TRIGGERS_COPY}
      />
      <SyncIssuesCard connectionId={connectionId} />
    </div>
  );
}

function RouteComponent() {
  const queryClient = useQueryClient();
  const { data: connectionsResponse } = useListFsmConnections();

  useDataRefresh(DataRefreshEvent.FsmConnectionConnected, () => {
    queryClient.invalidateQueries({
      queryKey: getListFsmConnectionsQueryKey(),
    });
  });

  const connections = connectionsResponse?.data ?? [];
  const skimmerConnection = connections.find(
    (c) => c.platform === FsmPlatform.Skimmer && c.isConnected,
  );

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Skimmer"
        parent={{ url: "/integrations", label: "Integrations" }}
        titleIcon={
          <img
            src={platformPresentation[FsmPlatform.Skimmer].logoUrl}
            alt="Skimmer"
            className="size-8 rounded-lg object-contain"
          />
        }
      />
      <PageContentContainer>
        {connectionsResponse === undefined ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : skimmerConnection ? (
          <ConnectedView connectionId={skimmerConnection.id} />
        ) : (
          <PairingCodeCard />
        )}
      </PageContentContainer>
    </AdminLayout>
  );
}
